import { TFunction } from 'i18next';

import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EAlertCondition, EAlertFrequency, IPriceAlert } from '@aqx-core/common/api/price-alert';
import {
  DEFAULT_POINTS_STEP,
  EPriceAlertFieldKeys,
  EPriceAlertModalMsg,
  EPriceAlertTab,
  MAX_ALERTS_FOR_ALL_SYMBOLS,
  MAX_ALERTS_PER_SYMBOL
} from '@aqx-core/common/constants/price-alert';
import { IChangeColors } from '@aqx-core/common/constants/styling';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { i18n } from '@aqx-core/common/i18n';

import { getDecimalForCompanyIntegration } from '../price/getDecimalFromPrice';
import { formatPercentageDiffValue, formatPriceValue } from '../price/valueFormatters';

export const getAlertConditionLabel = (condition: EAlertCondition, tFunc?: TFunction): string => {
  const t = (text: string) => {
    return tFunc?.(text) ?? i18n.t(text) ?? text;
  };

  const ALERT_CONDITION_LABEL_MAP: {
    [key in EAlertCondition]: () => string;
  } = {
    [EAlertCondition.ABOVE]: () => t('Price more than (≥)'),
    [EAlertCondition.BELOW]: () => t('Price less than (≤)'),
    [EAlertCondition.CROSS_ABOVE]: () => t('Price rises to (≥)'),
    [EAlertCondition.CROSS_BELOW]: () => t('Price falls to (≤)'),
    [EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES]: () => t('Positive daily change (%) reaches'),
    [EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES]: () => t('Negative daily change (%) reaches')
  };
  return ALERT_CONDITION_LABEL_MAP[condition]();
};

export const getAlertFrequencyLabel = (frequency: EAlertFrequency, tFunc?: TFunction): string => {
  const t = (text: string) => {
    return tFunc?.(text) ?? i18n.t(text) ?? text;
  };

  const ALERT_FREQUENCY_LABEL_MAP: {
    [key in EAlertFrequency]: () => string;
  } = {
    [EAlertFrequency.ONLY_ONCE]: () => t('Only Once'),
    [EAlertFrequency.PER_DAY]: () => t('Once Per Day'),
    [EAlertFrequency.ALWAYS]: () => t('Always')
  };
  return ALERT_FREQUENCY_LABEL_MAP[frequency]();
};

export const getTabsList = (isMobile?: boolean) => {
  const currentSymbolTestId = EDataTestId.PRICE_ALERT_MANAGEMENT_CURRENT_SYMBOL_TAB;
  const allSymbolsTestId = EDataTestId.PRICE_ALERT_MANAGEMENT_ALL_SYMBOLS_TAB;
  return [
    {
      label: i18n.t('Current Symbol'),
      value: EPriceAlertTab.CURRENT_SYMBOL,
      ...(isMobile ? { testID: currentSymbolTestId } : { dataTestId: currentSymbolTestId })
    },
    {
      label: i18n.t('All Symbols'),
      value: EPriceAlertTab.ALL_SYMBOLS,
      ...(isMobile ? { testID: allSymbolsTestId } : { dataTestId: allSymbolsTestId })
    }
  ];
};

export const getAlertLimitMsg = (
  maxAlertsPerSymbol: number = MAX_ALERTS_PER_SYMBOL,
  maxAlertsInTotal: number = MAX_ALERTS_FOR_ALL_SYMBOLS
) => {
  return i18n.t(
    'You may only create up to {{maxAlertsPerSymbol}} alerts for each symbol and up to {{maxAlertsInTotal}} total alerts for all symbols.',
    { maxAlertsPerSymbol, maxAlertsInTotal }
  );
};

export const getManageAlertsMsg = () => {
  return i18n.t(
    'You can select multiple alerts to be deleted. Deleted alerts will be removed permanently. You will no longer receive notifications from deleted price alerts.'
  );
};

export const getManagePriceAlertTitle = (currentTab: EPriceAlertTab) => {
  return currentTab === EPriceAlertTab.CURRENT_SYMBOL
    ? i18n.t('Manage Price Alerts')
    : i18n.t('Manage All Price Alerts');
};

export const getTargetValueIncrementDataTestId = () => {
  return `${EDataTestId.PRICE_ALERT_MANAGEMENT_TARGET_VALUE_INPUT}-increase`;
};

export const getTargetValueDecrementDataTestId = () => {
  return `${EDataTestId.PRICE_ALERT_MANAGEMENT_TARGET_VALUE_INPUT}-decrease`;
};

export const getDeleteAlertsModalContent = (
  noOfAlerts: number
): {
  title: string;
  description: string;
} => {
  const hasMoreThanOneAlert = noOfAlerts > 1;
  const title = hasMoreThanOneAlert
    ? i18n.t('Delete {{noOfAlerts}} Price Alerts', { noOfAlerts })
    : i18n.t('Delete Price Alert');
  const description = hasMoreThanOneAlert
    ? i18n.t('This action will delete the selected price alerts and cannot be undone.')
    : i18n.t('This action will delete the price alert and cannot be undone.');
  return { title, description };
};

export const getSymbolDisabledMsg = () => {
  return i18n.t(
    'This symbol has been temporarily disabled. Your existing alerts for this symbol will remain inactive until the symbol is re-enabled.'
  );
};

export const getPriceAlertDirectionColor = (condition: EAlertCondition, colors: IChangeColors): string => {
  switch (condition) {
    case EAlertCondition.BELOW:
    case EAlertCondition.CROSS_BELOW:
    case EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES:
      return colors.negative;
    case EAlertCondition.ABOVE:
    case EAlertCondition.CROSS_ABOVE:
    case EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES:
    default:
      return colors.positive;
  }
};

export const getFormattedTargetValue = (
  priceAlertInfo: Pick<IPriceAlert, 'condition' | 'targetPrice'>,
  targetSymbolDetail: Pick<ISymbolDetail, 'decimal' | 'tickRulesGroup'>
) => {
  const { condition, targetPrice } = priceAlertInfo;
  const { decimal, tickRulesGroup } = targetSymbolDetail;
  switch (condition) {
    case EAlertCondition.BELOW:
    case EAlertCondition.CROSS_BELOW:
    case EAlertCondition.ABOVE:
    case EAlertCondition.CROSS_ABOVE:
      return formatPriceValue(targetPrice, tickRulesGroup, decimal);
    case EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES:
    case EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES:
    default:
      return formatPercentageDiffValue(targetPrice);
  }
};

export const isPercentageCondition = (condition?: EAlertCondition): boolean => {
  return Boolean(
    condition &&
      [EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES, EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES].includes(condition)
  );
};

export const getPriceAlertFormTitle = (isEdit: boolean) => {
  return isEdit ? i18n.t('Edit Price Alert') : i18n.t('Create Price Alert');
};

export const getPriceAlertsEmptyStateLabel = (isAllSymbolsTab?: boolean) => {
  return isAllSymbolsTab ? i18n.t('No price alerts created.') : i18n.t('No price alerts created for this symbol.');
};

interface IPriceFormattingInfoProps {
  decimal?: ISymbolDetail['decimal'];
  isPercentCondition: boolean;
  pointStep?: ISymbolDetail['pointStep'];
  tickRulesGroup?: ISymbolDetail['tickRulesGroup'];
}

interface IPriceFormattingInfo {
  decimalsByIntegration: number;
  pointStep: number;
}

export const getPriceFormattingInfo = (props: IPriceFormattingInfoProps): IPriceFormattingInfo => {
  const { decimal, isPercentCondition, pointStep, tickRulesGroup } = props;
  if (!decimal || !pointStep || isPercentCondition) {
    const pointStep = isPercentCondition ? 0.01 : DEFAULT_POINTS_STEP;
    return {
      decimalsByIntegration: 2,
      pointStep
    };
  }

  const decimalsByIntegration = getDecimalForCompanyIntegration(pointStep, tickRulesGroup, decimal);
  return {
    decimalsByIntegration,
    pointStep
  };
};

interface IPriceAlertModalContent {
  description: string;
  testID: EDataTestId;
  title: string;
}

type TPriceAlertModalContentMap = Record<EPriceAlertModalMsg, () => IPriceAlertModalContent>;

export const getPriceAlertModalContent = (
  msgType: EPriceAlertModalMsg,
  symbolLimit?: number,
  accountLimit?: number
) => {
  const priceAlertModalContent: TPriceAlertModalContentMap = {
    [EPriceAlertModalMsg.DUPLICATED_PRICE_ALERT]: () => ({
      description: i18n.t(
        'You have already set an alert with these values. Please change the condition or target value.'
      ),
      testID: EDataTestId.PRICE_ALERT_MANAGEMENT_DUPLICATED_PRICE_ALERT,
      title: i18n.t('Duplicated Price Alert')
    }),
    [EPriceAlertModalMsg.PRICE_ALERT_LIMIT_REACHED]: () => ({
      description: `${getAlertLimitMsg(symbolLimit, accountLimit)} ${i18n.t('Please delete some existing alerts before creating more.')}`,
      testID: EDataTestId.PRICE_ALERT_MANAGEMENT_PRICE_ALERT_LIMIT_REACHED,
      title: i18n.t('Price Alert Limit Reached')
    }),
    [EPriceAlertModalMsg.SYMBOL_TEMPORARILY_DISABLED]: () => ({
      description: getSymbolDisabledMsg(),
      testID: EDataTestId.PRICE_ALERT_MANAGEMENT_SYMBOL_TEMPORARILY_DISABLED,
      title: i18n.t('Symbol Temporarily Disabled')
    })
  };
  return priceAlertModalContent[msgType]();
};

export const getDeleteBtnLabel = (selectedAlerts: string[]) => {
  const hasSelectedIds = selectedAlerts.length > 0;
  return hasSelectedIds ? i18n.t('Delete ({{noOfAlerts}})', { noOfAlerts: selectedAlerts.length }) : i18n.t('Delete');
};

interface IFieldLabelObj {
  name: string;
  placeholder: string;
}

export const getPriceAlertFieldLabel = (key: EPriceAlertFieldKeys) => {
  const FIELD_LABEL_MAP: {
    [key in EPriceAlertFieldKeys]: () => IFieldLabelObj;
  } = {
    [EPriceAlertFieldKeys.CONDITION]: () => ({
      name: i18n.t('Condition'),
      placeholder: i18n.t('Select a condition')
    }),
    [EPriceAlertFieldKeys.FREQUENCY]: () => ({
      name: i18n.t('Frequency'),
      placeholder: i18n.t('Select a frequency')
    }),
    [EPriceAlertFieldKeys.TARGET_VALUE]: () => ({
      name: i18n.t('Target Value'),
      placeholder: i18n.t('Enter a target value')
    }),
    [EPriceAlertFieldKeys.TARGET_VALUE_PERCENT]: () => ({
      name: i18n.t('Target Percentage (%)'),
      placeholder: i18n.t('Enter a target percentage (%)')
    })
  };
  return FIELD_LABEL_MAP[key]();
};

export const getTargetValueTooltip = () => i18n.t('The Target Value is based on Bid price.');
